/* eslint-disable @typescript-eslint/no-namespace */
import { Buffer } from "buffer";
import { create, IPFSHTTPClient, Options } from "ipfs-http-client";

// Define environment variable types
declare global {
  namespace NodeJS {
    interface ProcessEnv {
      REACT_APP_IPFS_PROJECT_ID?: string;
      REACT_APP_IPFS_PROJECT_SECRET?: string;
    }
  }
}

interface IPFSConfig extends Options {
  ProjectName: string;
}

// Lazy initialization function
let ipfsClient: IPFSHTTPClient | null = null;

const initializeIpfs = (): IPFSHTTPClient | null => {
  if (ipfsClient) {
    return ipfsClient;
  }

  const projectId: string | undefined = process.env.REACT_APP_IPFS_PROJECT_ID;
  const projectSecret: string | undefined =
    process.env.REACT_APP_IPFS_PROJECT_SECRET;

  if (!projectId || !projectSecret) {
    return null;
  }

  try {
    const auth: string =
      "Basic " +
      Buffer.from(projectId + ":" + projectSecret).toString("base64");

    ipfsClient = create({
      host: "ipfs.infura.io",
      port: 5001,
      protocol: "https",
      ProjectName: "blocsysdev",
      apiPath: "/api/v0",
      headers: {
        authorization: auth,
      },
    } as IPFSConfig);

    return ipfsClient;
  } catch (error) {
    return null;
  }
};

// Create a proxy object that initializes IPFS on first access
// This prevents errors on module load if env vars aren't available yet
const ipfs = new Proxy({} as IPFSHTTPClient, {
  get(_target, prop) {
    // Allow checking if IPFS is available without throwing
    if (prop === "isInitialized") {
      return () => {
        const client = initializeIpfs();
        return client !== null;
      };
    }

    const client = initializeIpfs();
    if (!client) {
      // Provide a helpful error message
      const hasProjectId = !!process.env.REACT_APP_IPFS_PROJECT_ID;
      const hasProjectSecret = !!process.env.REACT_APP_IPFS_PROJECT_SECRET;

      if (!hasProjectId || !hasProjectSecret) {
        throw new Error(
          `IPFS client is not initialized. Environment variables not found.\n` +
            `REACT_APP_IPFS_PROJECT_ID: ${hasProjectId ? "✓" : "✗"}\n` +
            `REACT_APP_IPFS_PROJECT_SECRET: ${hasProjectSecret ? "✓" : "✗"}\n` +
            `Please ensure you're running the app with: npm run start-local, npm run start-dev, or npm run start-prod`,
        );
      }

      throw new Error(
        "IPFS client failed to initialize. Please check your IPFS credentials.",
      );
    }
    const value = (client as any)[prop];
    if (typeof value === "function") {
      return value.bind(client);
    }
    return value;
  },
});

export default ipfs;
